// @noflow
import initI18n from '@/packs/localisation'
import type { Language } from '@/packs/localisation'
import * as amplitude from '@amplitude/analytics-browser'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import React from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Markup } from 'react-render-markup'

import { initBowlSliderAnimation } from '@/utils/animateBowlSlider'
import { DiscountBasis } from '@/utils/butternutbox/discountCodes'
import { initGoogleClickIdentifierSession } from '@/utils/googleClickIdentifierSession'
import { isDevelopment } from '@/utils/isDevelopment'
import { playVideosOnScroll } from '@/utils/playVideosOnScroll'
import * as Sentry from '@/utils/sentry'

import useWindowSize from '@/hooks/useWindowSize'

// Components
import { initCookieConsent } from '@/components/cookie_consent'
import BreedSelector from '@/components/shared/elements/BreedSelector'
import { BreedSelectorVariant } from '@/components/shared/elements/BreedSelector/BreedSelector'
import DogProfileSection from '@/components/shared/elements/DogProfileSection/DogProfileSection'
import { initFooterLeadCapture } from '@/components/shared/elements/FooterLeadCapture/FooterLeadCapture'
import RoughCostCalculatorCard from '@/components/shared/elements/RoughCostCalculator/RoughCostCalculatorCard'
import TestimonialsTrustpilot from '@/components/shared/elements/TestimonialsTrustpilot/TestimonialsTrustpilot'
import WonkyTitleText from '@/components/shared/elements/WonkyTitleText/WonkyTitleText'
import initGeoIpWidget from '@/components/shared/geo_ip_widget/initGeoIpWidget'
import { BREAKPOINTS } from '@/components/templates/Base'

import type { Code as CountryCode } from '@/shared_types/rails_models/shipping_countries'

const dataContainer = document.querySelector('#home-content-tag') as HTMLElement
if (!dataContainer)
  Sentry.captureException('Could not find #home-content-tag', {
    tags: {
      product: Sentry.Product.HomePage
    }
  })

const { dataset } = dataContainer
const {
  language,
  isPrewizardGuest,
  discountBasis,
  discountFirstPartValue,
  shippingCountryCode,
  pendingSubscriptionData,
  dogsData,
  ukOrIrelandUser,
  isMobile,
  isPsibufet
} = dataset

const preferredLanguage = (language as Language) || 'en'
const countryCode = (shippingCountryCode as CountryCode) || 'GB'

const amplitudeApiKey = process.env.AMPLITUDE_API_KEY
const sessionReplayTracking = sessionReplayPlugin({
  debugMode: isDevelopment(),
  sampleRate: 0.2
})
amplitude.add(sessionReplayTracking)

const initLanguage = (): void => {
  initI18n(preferredLanguage)
}

const initBreedSelector = (): void => {
  const breedSelectorElement =
    document.getElementsByClassName('breed-selector')[0]

  if (!breedSelectorElement) return

  const ctaDestination =
    isPrewizardGuest === 'true' ? '/wizard/new' : '/plans/recipes'

  const BreedSelectorComponent = () => {
    const { windowWidth } = useWindowSize()

    return (
      <BreedSelector
        isPreWizardGuest={isPrewizardGuest === 'true'}
        ctaDestination={ctaDestination}
        variant={BreedSelectorVariant.Homepage}
        scrollToOnFocus={
          windowWidth <= BREAKPOINTS.lg ? { topOffset: 60 } : false
        }
        screenIdentifier="homepage"
      />
    )
  }

  ReactDOM.render(<BreedSelectorComponent />, breedSelectorElement)
}

const DynamicTitle = () => {
  const { t } = useTranslation()

  return (
    <Markup
      allowed={['br']}
      markup={t('homepage:hero.title_summer_campaign_2025_html')}
    />
  )
}

const initDynamicTitle = (elementClassName: string) => {
  const dynamicTitleElements = document.getElementsByClassName(elementClassName)

  const dynamicTitleElementsArray = [...dynamicTitleElements]

  if (!dynamicTitleElements) return null

  Array.from(dynamicTitleElementsArray).forEach((element) => {
    ReactDOM.render(<DynamicTitle />, element)
  })
}

const initWonkyTitleText = (elementClassName: string, titleText: string) => {
  const wonkyTextElements = document.getElementsByClassName(elementClassName)

  const wonkyTextElementArray = [...wonkyTextElements]

  if (!wonkyTextElements) return null

  wonkyTextElementArray.forEach((element) => {
    ReactDOM.render(
      <WonkyTitleText namespace="home_page" text={titleText} />,
      element
    )
  })
}

const initDogProfileSection = () => {
  const dogProfileSectionElement = document.getElementById(
    'dog-profile-section'
  ) as HTMLElement

  if (!dogProfileSectionElement) return null

  // Parse the pending subscription data from JSON
  let parsedPendingSubscriptionData = null
  if (pendingSubscriptionData) {
    try {
      parsedPendingSubscriptionData = JSON.parse(pendingSubscriptionData)
    } catch (error) {
      Sentry.captureException('Failed to parse pending subscription data', {
        tags: {
          product: Sentry.Product.HomePage
        }
      })

      return null
    }
  }

  // Parse the dogs data from JSON
  let parsedDogsData = null
  if (dogsData) {
    try {
      parsedDogsData = JSON.parse(dogsData)
    } catch (error) {
      Sentry.captureException('Failed to parse dogs data', {
        tags: {
          product: Sentry.Product.HomePage
        }
      })

      return null
    }
  }

  ReactDOM.render(
    <DogProfileSection
      pendingSubscriptionData={parsedPendingSubscriptionData}
      dogsData={parsedDogsData}
    />,
    dogProfileSectionElement
  )
}

const initRoughCostCalculatorCard = () => {
  const roughCostCalculatorElement = document.getElementById(
    'rough-cost-calculator-card'
  ) as HTMLElement

  // We return null for users who aren't shown the calculator
  if (!roughCostCalculatorElement) return null

  ReactDOM.render(
    <RoughCostCalculatorCard
      preferredLanguage={preferredLanguage}
      screenIdentifier="homepage"
      discountBasis={discountBasis as DiscountBasis}
      discountValue={parseInt(discountFirstPartValue || '') || 0}
      shippingCountryCode={shippingCountryCode as CountryCode}
    />,
    roughCostCalculatorElement
  )
}

const initTestimonialsTrustpilot = (): void => {
  const container = document.querySelector('.testimonials__trustpilot')
  if (!container) return

  ReactDOM.render(
    <TestimonialsTrustpilot
      shippingCountryCode={countryCode}
      isPsibufet={isPsibufet === 'true'}
    />,
    container
  )
}

document.addEventListener('DOMContentLoaded', (): void => {
  const className = isMobile === 'true' ? 'title-text-mobile' : 'title-text'
  const title =
    isMobile === 'true' ? 'hero_section.title_mobile' : 'hero_section.title'

  initLanguage()

  /**
   * Homepage Title Selection Logic
   *
   * The homepage displays different title content based on user location and language:
   *
   * 1. UK/Ireland Users (ukOrIrelandUser === 'true'):
   *    - Shows DynamicTitle component with summer campaign content
   *    - Uses translation key: 'homepage:hero.title_summer_campaign_2025_html'
   *    - Applies to GB, NI, and IE country codes
   *
   * 2. Non-UK/Ireland Users (ukOrIrelandUser === 'false' or undefined):
   *    - Shows WonkyTitleText component with localized content
   *    - English users: Use 'hero_section.title_v2' (alternative English title)
   *    - Other languages: Use standard title key based on device:
   *      - Mobile: 'hero_section.title_mobile'
   *      - Desktop: 'hero_section.title'
   *
   * Note: ukOrIrelandUser is a string ('true'/'false') from HTML dataset,
   * not a boolean, so strict equality check (===) is required.
   */
  if (ukOrIrelandUser === 'true') {
    initDynamicTitle(className)
  } else {
    initWonkyTitleText(
      className,
      preferredLanguage === 'en' ? 'hero_section.title_v2' : title
    )
  }

  initWonkyTitleText(
    'wonky-title-text-returning-guest',
    'hero_section.title_returning_guest'
  )
  if (!window.matchMedia('(prefers-reduced-motion)').matches) {
    initBowlSliderAnimation()
  }
  initTestimonialsTrustpilot()
  initRoughCostCalculatorCard()
  initDogProfileSection()
  initBreedSelector()
  initGoogleClickIdentifierSession()
  initGeoIpWidget({
    desktopNav: true,
    mobileSidebar: true,
    contentTagElementId: 'home-content-tag'
  })
  initCookieConsent()
  playVideosOnScroll()
  initFooterLeadCapture()

  !isDevelopment() && amplitudeApiKey && amplitude.init(amplitudeApiKey)
})
